// Task writes go through tenax_create_task and tenax_update_task (task 1.2,
// D-028), so author, channel and the task rules are the same for every
// screen, Atomic's included. These helpers turn react-admin data into the
// arguments of the two functions.

type TaskData = Record<string, unknown>;

export type TaskScope =
  | "contact"
  | "company"
  | "deal"
  | "agreement"
  | "order"
  | "internal";

const LINK_BY_SCOPE: Record<Exclude<TaskScope, "internal">, string> = {
  contact: "contact_id",
  company: "company_id",
  deal: "deal_id",
  agreement: "agreement_id",
  order: "order_id",
};

// Record field -> tenax_update_task key
const UPDATE_KEYS: Record<string, string> = {
  text: "title",
  description: "description",
  status: "status",
  due_date: "due_date",
  sales_id: "assignee",
  priority: "priority",
  area_id: "area",
  type: "type",
};

// Columns the app never writes: changed values here are not user edits
const READ_ONLY_FIELDS = new Set([
  "id",
  "created_at",
  "updated_at",
  "created_by",
  "fts",
  "origin",
  "origin_ref",
  "auto_reason",
  "template_id",
]);

export const RELINK_ERROR = "tenax.tasks.errors.relink";

const scopeOf = (data: TaskData): TaskScope => {
  if (typeof data.scope === "string") return data.scope as TaskScope;
  const linked = Object.entries(LINK_BY_SCOPE).find(
    ([, field]) => data[field] != null,
  );
  return (linked?.[0] as TaskScope | undefined) ?? "internal";
};

export const toCreateTaskArgs = (data: TaskData) => {
  const scope = scopeOf(data);
  return {
    p_title: data.text,
    p_scope: scope,
    p_linked_id: scope === "internal" ? null : data[LINK_BY_SCOPE[scope]],
    p_description: data.description ?? null,
    p_assignee: data.sales_id ?? null,
    p_due_date: data.due_date ?? null,
    p_priority: data.priority ?? 2,
    p_type: data.type ?? null,
    p_area_id: data.area_id ?? null,
  };
};

const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b || (a == null && b == null)) return true;
  if (typeof a === "string" && typeof b === "string") {
    const [timeA, timeB] = [Date.parse(a), Date.parse(b)];
    return !Number.isNaN(timeA) && timeA === timeB;
  }
  return false;
};

// Only the fields that really changed. Atomic marks a task done with
// done_date: it becomes a status change. A changed link (contact, deal…)
// is refused: a task for another record is a new task.
export const toUpdateTaskChanges = (
  data: TaskData,
  previousData: TaskData,
): Record<string, unknown> => {
  const changes: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(data)) {
    if (READ_ONLY_FIELDS.has(field) || sameValue(value, previousData[field])) {
      continue;
    }
    const key = UPDATE_KEYS[field];
    if (key) {
      changes[key] = value;
    } else if (field === "done_date") {
      const isStatusChanged =
        "status" in data && !sameValue(data.status, previousData.status);
      if (!isStatusChanged) {
        changes.status = value != null ? "done" : "todo";
      }
    } else {
      throw new Error(RELINK_ERROR);
    }
  }
  return changes;
};
